import React from 'react';
import styled from 'astroturf/react';
import Modal from 'js/components/modals/Modal';
import { Button } from 'js/design_system/Button';

import AcademiaPremiumLogo from 'js/svg/colorless/logo_academia_premium.svg';
import AccessIcon from 'js/svg/colorful/padlock_academia.svg';
import DownloadIcon from 'js/svg/colorful/folder_papers_down_arrow.svg';
import MentionIcon from 'js/svg/colorful/cited_paper.svg';
import WorldwideIcon from 'js/svg/colorful/search_globe.svg';
import OxfordLogo from 'js/svg/colorless/logo_oxford.svg';
import BerkeleyLogo from 'js/svg/colorless/logo_berkeley.svg';
import UnamLogo from 'js/svg/colorless/logo_unam.svg';

const StyledModal = styled(Modal)`
  padding: 0;
  max-width: 844px;
  background-color: #f8f8f8;
  border-radius: 4px;
  width: calc(100% - 32px);
  margin: 40px auto;

  @media (max-width: 567px) {
    margin: 32px auto;
  }
`;

const TopContainer = styled.div`
  background-image: url('/images/modals/backgrounds/jellyfish.webp');
  background-size: cover;
  position: relative;
  padding: 52px 0;
  border-radius: 4px 4px 0 0;

  @media (max-width: 567px) {
    padding: 32px 0;
  }
`;

const Banner = styled.div`
  background-color: #fff;
  border-radius: 4px;
  width: calc(100% - 120px);
  margin: 0 auto;
  padding: 28px;
  text-align: center;

  @media (max-width: 567px) {
    width: calc(100% - 48px);
  }
`;

const Logo = styled.div`
  width: 200px;
  height: 15px;
  margin: 0 auto;
  color: #222233;
  svg {
    width: inherit;
    height: inherit;
    vertical-align: middle;
  }
`;

const Text = styled.div`
  font-size: 32px;
  line-height: 44px;
  font-weight: 500;
  padding: 32px 0;
  color: #0645b1;
  margin: 0 auto;
  max-width: 394px;

  @media (max-width: 567px) {
    font-size: 24px;
    line-height: 32px;
    max-width: 244px;
  }
`;

const ActionButton = styled(Button)`
  margin: 0 auto;

  @media (max-width: 567px) {
    padding: 0 10px;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 24px;
  padding: 8px;
  color: #fff;
  cursor: pointer;
  &:hover {
    color: #ccc;
  }

  @media (max-width: 567px) {
    display: none;
  }
`;

const BottomContainer = styled.div`
  padding: 16px;
  text-align: center;
`;

const Reasons = styled.div`
  padding: 16px 0;
`;

const Question = styled.div`
  font-size: 24px;
  line-height: 32px;
`;

const ReasonList = styled.div`
  display: flex;
  justify-content: center;
`;

const Reason = styled.div`
  padding: 16px 36px;
  text-align: center;
  width: 45%;
`;

const ReasonIcon = styled.div`
  margin: 16px 0;
  height: 54px;
  svg {
    width: inherit;
    height: inherit;
  }
`;

const ReasonTitle = styled.div`
  font-size: 16px;
  line-height: 20px;
  font-weight: 500;
  padding: 12px 0;
`;

const ReasonText = styled.div`
  font-size: 14px;
  line-height: 20px;
`;

const Universities = styled.div`
  border-top: 1px solid #b1b1ba;
  text-align: center;
  padding-top: 28px;
`;

const UniversityTitle = styled.div`
  font-size: 16px;
  line-height: 28px;
  font-weight: 500;
`;

const UniversityList = styled.div`
  display: flex;
  justify-content: space-evenly;
  padding: 18px;

  @media (max-width: 567px) {
    flex-direction: column;
  }
`;
const UniversityIcon = styled.div`
  width: 150px;
  height: 45px;
  color: #222233;

  @media (max-width: 567px) {
    margin: 16px auto;
  }

  svg {
    width: inherit;
    height: inherit;
  }
`;

type Props = {
  ctaText: string;
  buttonText: string;
  clickThrough: () => void;
  hide: () => void;
};

export const CommonModal = ({
  ctaText,
  buttonText,
  clickThrough,
  hide,
}: Props) => {
  return (
    <StyledModal onClose={hide}>
      <TopContainer>
        <Banner>
          <Logo>
            <AcademiaPremiumLogo />
          </Logo>
          <Text>{ctaText}</Text>
          <ActionButton onClick={clickThrough}>{buttonText}</ActionButton>
        </Banner>
        <CloseButton onClick={hide}>&times;</CloseButton>
      </TopContainer>
      <BottomContainer>
        <Reasons>
          <Question>Why reactivate Premium?</Question>
          <ReasonList>
            <Reason>
              <ReasonIcon>
                <MentionIcon />
              </ReasonIcon>
              <ReasonTitle>See the papers that mention you</ReasonTitle>
              <ReasonText>
                Get notified when you’re cited or referenced with Mentions.
              </ReasonText>
            </Reason>
            <Reason>
              <ReasonIcon>
                <DownloadIcon />
              </ReasonIcon>
              <ReasonTitle>Download unlimited PDF packages</ReasonTitle>
              <ReasonText>
                Get all the papers you need in one click. Save time doing
                research.
              </ReasonText>
            </Reason>
          </ReasonList>
          <ReasonList>
            <Reason>
              <ReasonIcon>
                <AccessIcon />
              </ReasonIcon>
              <ReasonTitle>Access all Premium features</ReasonTitle>
              <ReasonText>
                Search full texts of millions of papers, track your impact, and
                much more.
              </ReasonText>
            </Reason>
            <Reason>
              <ReasonIcon>
                <WorldwideIcon />
              </ReasonIcon>
              <ReasonTitle>Support researchers worldwide</ReasonTitle>
              <ReasonText>
                Your subscription helps keep access to research free to all.
              </ReasonText>
            </Reason>
          </ReasonList>
        </Reasons>
        <Universities>
          <UniversityTitle>
            Thousands of researchers have upgraded to Academia Premium
          </UniversityTitle>
          <UniversityList>
            <UniversityIcon>
              <OxfordLogo />
            </UniversityIcon>
            <UniversityIcon>
              <BerkeleyLogo />
            </UniversityIcon>
            <UniversityIcon>
              <UnamLogo />
            </UniversityIcon>
          </UniversityList>
        </Universities>
      </BottomContainer>
    </StyledModal>
  );
};
